import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useEditorState } from '@tiptap/react';
import Icon from '../AppIcon';

const ToolbarButton = ({ onClick, onMouseDown, active, disabled, title, children, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    onMouseDown={onMouseDown}
    disabled={disabled}
    title={title}
    className={`p-2 rounded-lg transition-colors ${
      active ? 'bg-[var(--color-accent)]/20 text-[var(--color-accent)]' : 'hover:bg-gray-100 text-gray-600'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
  >
    {children}
  </button>
);

const ToolbarDivider = () => <div className="w-px h-6 bg-gray-200 mx-1" />;

const EditorToolbar = forwardRef(function EditorToolbar({ editor, onInsertImage, onInsertAudio }, ref) {
  useEditorState({
    editor,
    selector: (snapshot) => ({
      selection: snapshot.editor?.state.selection,
      transactionNumber: snapshot.transactionNumber,
    }),
  });
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [captionValue, setCaptionValue] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const savedInsertPosRef = useRef(null);

  const imageAttrs = editor?.getAttributes('image') || {};
  const isImageSelected = editor?.isActive('image') ?? false;

  useEffect(() => {
    if (isImageSelected) {
      setCaptionValue(imageAttrs.caption || '');
    } else {
      setCaptionValue('');
    }
  }, [isImageSelected, imageAttrs.caption]);

  const handleCaptionChange = (e) => {
    const val = e.target.value;
    setCaptionValue(val);
    if (isImageSelected && editor) {
      editor.chain().updateAttributes('image', { caption: val || null }).run();
    }
  };

  if (!editor) return null;
  const isLinkBlockSelected = editor.isActive('linkBlock');
  const isInlineLinkActive = editor.isActive('link');
  const linkBlockAttrs = editor.getAttributes('linkBlock');
  const linkMarkAttrs = editor.getAttributes('link');

  const handleInsertLink = () => {
    if (linkUrl.trim()) {
      const text = linkText.trim() || linkUrl;
      if (isLinkBlockSelected) {
        editor.chain().focus().updateAttributes('linkBlock', { href: linkUrl, text }).run();
      } else if (isInlineLinkActive) {
        const escapedUrl = linkUrl.replace(/"/g, '&quot;');
        const escapedText = text.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        const linkHtml = `<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer">${escapedText}</a>`;
        editor.chain().focus().extendMarkRange('link').insertContent(linkHtml).unsetMark('link').run();
      } else {
        const pos = savedInsertPosRef.current;
        const escapedUrl = linkUrl.replace(/"/g, '&quot;');
        const escapedText = text.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        const linkHtml = `<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer">${escapedText}</a>`;
        if (pos != null) {
          editor.chain().focus().insertContentAt(pos, linkHtml).unsetMark('link').run();
        } else {
          editor.chain().focus().insertContent(linkHtml).unsetMark('link').run();
        }
      }
      setLinkDialogOpen(false);
      setLinkUrl('');
      setLinkText('');
      savedInsertPosRef.current = null;
    }
  };

  const openLinkDialogForEdit = () => {
    if (isLinkBlockSelected) {
      setLinkUrl(linkBlockAttrs.href || '');
      setLinkText(linkBlockAttrs.text || '');
    } else if (isInlineLinkActive) {
      editor.chain().focus().extendMarkRange('link').run();
      const { from, to } = editor.state.selection;
      const linkText = editor.state.doc.textBetween(from, to, ' ');
      setLinkUrl(linkMarkAttrs.href || '');
      setLinkText(linkText || '');
    }
    savedInsertPosRef.current = null;
    setLinkDialogOpen(true);
  };

  const openLinkEditRef = useRef(openLinkDialogForEdit);
  openLinkEditRef.current = openLinkDialogForEdit;
  useImperativeHandle(ref, () => ({ openLinkEdit: () => openLinkEditRef.current?.() }), []);

  const openLinkDialogForInsert = (e) => {
    e?.preventDefault?.();
    savedInsertPosRef.current = editor.state.selection.from;
    setLinkUrl('');
    setLinkText('');
    setLinkDialogOpen(true);
  };

  return (
    <div
      className="relative flex flex-wrap items-center gap-1 p-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl shadow-sm sticky top-20 z-40"
      dir="rtl"
    >
      {/* Text formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        title="عريض (Ctrl+B)"
      >
        <Icon name="Bold" size={18} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        title="مائل (Ctrl+I)"
      >
        <Icon name="Italic" size={18} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive('underline')}
        title="تحته خط"
      >
        <Icon name="Underline" size={18} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        active={editor.isActive('strike')}
        title="يتوسطه خط"
      >
        <Icon name="Strikethrough" size={18} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Headings */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        active={editor.isActive('heading', { level: 1 })}
        title="عنوان 1"
      >
        <span className="font-bold text-sm">H1</span>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive('heading', { level: 2 })}
        title="عنوان 2"
      >
        <span className="font-bold text-sm">H2</span>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        active={editor.isActive('heading', { level: 3 })}
        title="عنوان 3"
      >
        <span className="font-bold text-sm">H3</span>
      </ToolbarButton>

      <ToolbarDivider />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        title="قائمة نقطية"
      >
        <Icon name="List" size={18} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        title="قائمة مرقمة"
      >
        <Icon name="ListOrdered" size={18} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        active={editor.isActive('blockquote')}
        title="اقتباس"
      >
        <Icon name="Quote" size={18} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Alignment */}
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        active={editor.isActive({ textAlign: 'right' })}
        title="محاذاة لليمين"
      >
        <Icon name="AlignRight" size={18} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        active={editor.isActive({ textAlign: 'center' })}
        title="توسيط"
      >
        <Icon name="AlignCenter" size={18} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        active={editor.isActive({ textAlign: 'left' })}
        title="محاذاة لليسار"
      >
        <Icon name="AlignLeft" size={18} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Insert */}
      <ToolbarButton onClick={() => onInsertImage?.()} title="إدراج صورة">
        <Icon name="Image" size={18} />
      </ToolbarButton>
      <ToolbarButton onClick={() => onInsertAudio?.()} title="إدراج صوت">
        <Icon name="Music" size={18} />
      </ToolbarButton>
      <ToolbarButton
        onMouseDown={(e) => !isLinkBlockSelected && !isInlineLinkActive && openLinkDialogForInsert(e)}
        onClick={() => (isLinkBlockSelected || isInlineLinkActive) && openLinkDialogForEdit()}
        active={isLinkBlockSelected || isInlineLinkActive}
        title={isLinkBlockSelected || isInlineLinkActive ? 'تعديل الرابط' : 'إدراج رابط'}
      >
        <Icon name="Link" size={18} />
      </ToolbarButton>
      {(isLinkBlockSelected || isInlineLinkActive) && (
        <ToolbarButton
          onClick={() => {
            if (isInlineLinkActive) {
              editor.chain().focus().extendMarkRange('link').unsetLink().run();
            } else {
              editor.chain().focus().deleteSelection().run();
            }
          }}
          title="حذف الرابط"
          className="!text-red-500 hover:!bg-red-50"
        >
          <Icon name="Trash2" size={18} />
        </ToolbarButton>
      )}

      {/* Image alignment and caption when image selected */}
      {isImageSelected && (
        <>
          <ToolbarDivider />
          <ToolbarButton
            onClick={() => editor.chain().focus().updateAttributes('image', { align: 'right' }).run()}
            active={imageAttrs.align === 'right'}
            title="محاذاة لليمين"
          >
            <Icon name="AlignRight" size={18} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().updateAttributes('image', { align: 'center' }).run()}
            active={imageAttrs.align === 'center'}
            title="توسيط"
          >
            <Icon name="AlignCenter" size={18} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().updateAttributes('image', { align: 'left' }).run()}
            active={imageAttrs.align === 'left'}
            title="محاذاة لليسار"
          >
            <Icon name="AlignLeft" size={18} />
          </ToolbarButton>
          <ToolbarDivider />
          <ToolbarButton
            onClick={() => {
              const r = (imageAttrs.rotate ?? 0) - 90;
              editor.chain().focus().updateAttributes('image', { rotate: r < 0 ? r + 360 : r }).run();
            }}
            title="تدوير لليسار 90°"
          >
            <Icon name="RotateCcw" size={18} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              const r = (imageAttrs.rotate ?? 0) + 90;
              editor.chain().focus().updateAttributes('image', { rotate: r >= 360 ? r - 360 : r }).run();
            }}
            title="تدوير لليمين 90°"
          >
            <Icon name="RotateCw" size={18} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().deleteSelection().run()}
            title="حذف الصورة"
            className="!text-red-500 hover:!bg-red-50"
          >
            <Icon name="Trash2" size={18} />
          </ToolbarButton>
          <div className="flex items-center gap-2 px-2" dir="rtl">
            <input
              type="text"
              value={captionValue}
              onChange={handleCaptionChange}
              placeholder="تسمية توضيحية..."
              className="text-sm px-2 py-1 border border-gray-200 rounded w-40"
              style={{ fontFamily: 'Amiri, serif' }}
            />
          </div>
        </>
      )}

      {/* Link insert dialog */}
      {linkDialogOpen && (
        <div className="absolute top-full right-0 mt-2 p-3 bg-white border border-gray-200 rounded-xl shadow-lg z-50 min-w-[280px]" dir="rtl">
          <div className="space-y-2">
            <input
              type="text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              placeholder="نص الرابط"
              className="w-full px-3 py-2 border rounded-lg text-sm"
              style={{ fontFamily: 'Amiri, serif' }}
            />
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border rounded-lg text-sm"
              dir="ltr"
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setLinkDialogOpen(false); setLinkUrl(''); setLinkText(''); }}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
              >
                إلغاء
              </button>
              <button
                type="button"
                onClick={handleInsertLink}
                disabled={!linkUrl.trim()}
                className="px-3 py-1 text-sm bg-[var(--color-accent)] text-white rounded disabled:opacity-50"
              >
                إدراج
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default EditorToolbar;
